import React, { useState } from "react";
import { toast } from "sonner";
import { useI18n } from "../i18n";
import { api } from "../api";
import { Briefcase, Users, Shield, CheckCircle2, ArrowRight } from "lucide-react";

const Business = () => {
  const { lang } = useI18n();
  const isPt = lang === "pt";
  const [form, setForm] = useState({
    company: "",
    contact_name: "",
    email: "",
    phone: "",
    fleet_size: "1-3",
    use_case: "",
    monthly_budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/business/inquiry", form);
      setSent(true);
      toast.success(
        isPt
          ? "Solicitação recebida — retornamos em 1 dia útil."
          : "Inquiry received — we'll reply within 1 business day."
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20" data-testid="business-page">
      <p className="text-label text-[#FFB400]">
        <span className="line-accent" />
        {isPt ? "Movix Business" : "Movix Business"}
      </p>
      <h1 className="mt-4 font-heading text-5xl sm:text-6xl font-bold tracking-tighter text-white max-w-4xl">
        {isPt
          ? "Frota corporativa por mensalidade fixa."
          : "Corporate fleet at a flat monthly rate."}
      </h1>
      <p className="mt-6 max-w-2xl text-zinc-300 text-lg leading-relaxed">
        {isPt
          ? "Para construtoras, restaurantes, escolas e PMEs da Grande Boston. Em vez de comprar carros, contrate a Movix: 1 ou 10 veículos, mensalidade fixa, troca quando precisar, seguro incluso, manutenção zero do seu lado."
          : "For construction firms, restaurants, schools and SMBs in Greater Boston. Instead of buying cars, hire Movix: 1 to 10 vehicles, flat monthly rate, swap as needed, insurance included, zero maintenance on your side."}
      </p>

      {/* Benefits */}
      <div
        className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/10 border border-white/10"
        data-testid="business-benefits"
      >
        {[
          {
            icon: Briefcase,
            t: isPt ? "Conta única" : "Single invoice",
            d: isPt
              ? "Fatura consolidada mensal, recibo aceito para imposto de renda."
              : "One consolidated monthly invoice, tax-deductible receipt.",
          },
          {
            icon: Users,
            t: isPt ? "Múltiplos motoristas" : "Multiple drivers",
            d: isPt
              ? "Adicione e remova motoristas do seu time sem taxa."
              : "Add or remove drivers from your team at no charge.",
          },
          {
            icon: Shield,
            t: isPt ? "SLA e prioridade" : "SLA & priority",
            d: isPt
              ? "Reposição de veículo em até 2h. Atendimento dedicado."
              : "Replacement vehicle in 2 hours. Dedicated support line.",
          },
        ].map((b, i) => (
          <div key={i} className="bg-[#0a0a0a] p-8">
            <b.icon size={28} className="text-[#FFB400]" />
            <h3 className="mt-4 font-heading text-xl font-medium text-white">{b.t}</h3>
            <p className="mt-2 text-zinc-400 text-sm">{b.d}</p>
          </div>
        ))}
      </div>

      {/* Form */}
      <div className="mt-20 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-5">
          <h2 className="font-heading text-3xl font-semibold text-white tracking-tight">
            {isPt
              ? "Conte do seu negócio — mandamos uma cotação na hora."
              : "Tell us about your business — we'll send a quote right away."}
          </h2>
          <ul className="mt-8 space-y-3 text-zinc-300">
            {[
              isPt ? "Resposta em até 1 dia útil" : "Reply within 1 business day",
              isPt ? "Sem comprometimento — primeira reunião é grátis" : "No commitment — first meeting is free",
              isPt ? "Setup completo em 7 dias" : "Full setup in 7 days",
              isPt ? "Cancela quando quiser depois do 1º mês" : "Cancel anytime after the 1st month",
            ].map((p, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-[#FFB400] mt-0.5 flex-shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>

        {sent ? (
          <div className="lg:col-span-7 bg-[#0d0d0d] border border-[#FFB400] p-10" data-testid="business-success">
            <CheckCircle2 size={36} className="text-[#FFB400]" />
            <h3 className="mt-4 font-heading text-3xl font-semibold text-white">
              {isPt ? "Solicitação enviada!" : "Inquiry sent!"}
            </h3>
            <p className="mt-3 text-zinc-300">
              {isPt
                ? "A equipe Movix Business vai entrar em contato em até 1 dia útil. Enquanto isso, dê uma olhada na frota:"
                : "The Movix Business team will reach out within 1 business day. In the meantime, take a look at the fleet:"}
            </p>
            <a href="/fleet" className="btn-primary inline-flex mt-6">
              {isPt ? "Ver frota" : "View fleet"} <ArrowRight size={16} />
            </a>
          </div>
        ) : (
          <form
            onSubmit={onSubmit}
            className="lg:col-span-7 bg-[#0d0d0d] border border-white/10 p-8 grid grid-cols-1 md:grid-cols-2 gap-5"
            data-testid="business-form"
          >
            <div>
              <label className="text-label block mb-2">{isPt ? "Empresa" : "Company"} *</label>
              <input className="movix-input" required value={form.company} onChange={onChange("company")} data-testid="biz-company" />
            </div>
            <div>
              <label className="text-label block mb-2">{isPt ? "Seu nome" : "Your name"} *</label>
              <input className="movix-input" required value={form.contact_name} onChange={onChange("contact_name")} data-testid="biz-name" />
            </div>
            <div>
              <label className="text-label block mb-2">E-mail *</label>
              <input type="email" className="movix-input" required value={form.email} onChange={onChange("email")} data-testid="biz-email" />
            </div>
            <div>
              <label className="text-label block mb-2">{isPt ? "Telefone" : "Phone"} *</label>
              <input className="movix-input" required value={form.phone} onChange={onChange("phone")} data-testid="biz-phone" />
            </div>
            <div>
              <label className="text-label block mb-2">{isPt ? "Tamanho da frota" : "Fleet size"}</label>
              <select className="movix-input cursor-pointer" value={form.fleet_size} onChange={onChange("fleet_size")} data-testid="biz-fleet-size">
                <option value="1-3" className="bg-[#0a0a0a]">1–3 {isPt ? "veículos" : "vehicles"}</option>
                <option value="4-10" className="bg-[#0a0a0a]">4–10</option>
                <option value="11-25" className="bg-[#0a0a0a]">11–25</option>
                <option value="25+" className="bg-[#0a0a0a]">25+</option>
              </select>
            </div>
            <div>
              <label className="text-label block mb-2">{isPt ? "Uso principal" : "Primary use case"}</label>
              <input className="movix-input" placeholder={isPt ? "Ex.: equipe de obra" : "e.g. construction crew"} value={form.use_case} onChange={onChange("use_case")} data-testid="biz-usecase" />
            </div>
            <div className="md:col-span-2">
              <label className="text-label block mb-2">{isPt ? "Mensagem" : "Message"}</label>
              <textarea rows={4} className="movix-input" value={form.message} onChange={onChange("message")} data-testid="biz-message" />
            </div>
            <div className="md:col-span-2">
              <button type="submit" disabled={submitting} className="btn-primary w-full justify-center disabled:opacity-60" data-testid="biz-submit">
                {submitting ? (isPt ? "Enviando…" : "Sending…") : isPt ? "Pedir cotação" : "Request quote"}
                <ArrowRight size={16} />
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default Business;
